<template>
  <div class="segmentBigItem" v-show="mustShow">
    <b-dropdown ref="dropdown" position="is-top-right" trap-focus
                animation="zoom" :mobile-modal="false"
                :close-on-click="false"
                class="dropDownTimeLineBigElement"
                @active-change="activeChange"
    >
      <div slot="trigger" class="dropDownTrigger">
        <w-tool-tip :enlarge="true" style="width: 100%;height: 100%;"
                    :label="getTooltipForSection(segment.start)">
          <ImageSection :segment="segment" :class="showPreview||isSelected?'withBorder':''"
          />
        </w-tool-tip>
        <div class="editSectionButton" v-if="canEdit" @click.stop="editSection(false)"><w-icon icon="pencil" size="small"/> </div>
        <div class="buttonDeleteSection" v-if="deleteAvailable" @click.stop="deleteASection(segment.start.time)">
          <w-icon color="white" class="iconClose" icon="delete" size="small"></w-icon>
        </div>
        <div class="titleSection">
          {{ getTitleForSection(segment.start) }}
        </div>
        <div class="timeSection">
          {{ getHumanTime }}
        </div>
      </div>
      <b-dropdown-item
          :focusable="false"
          custom
          class="playerSectionContainer" :style="playerOffsetStyle">
        <SectionTitle v-if="getWeet!==undefined" :time="segment.start.time" :weet="getWeet"/>
        <div class="sectionPlayer">
          <SectionPlayer v-if="showPreview " :segment="segment"
                         :medias="getMedia"/>
        </div>
        <w-button class="close"
                  :circle="true" color="light"
                  icon="close" size="small" @click="closeDropDown()"
        />
        <w-button v-if="buttonLabel" class="buttonAction" size="small" @click="actionButton(segment)">
          {{buttonLabel}}
        </w-button>
        <w-button v-if="canEdit" @click="editSection(true)" class="buttonAction" size="small">
          {{$t('sectionEdit.editSection')}}
        </w-button>
      </b-dropdown-item>

    </b-dropdown>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import store from "@/store";
import { Segment, TimeEvent } from "@/store/timeLine/timeEvent";
import WToolTip from "@/components/wrapper/w-toolTip.vue";
import prettyMilliseconds from "pretty-ms";
import SectionPlayer from "@/components/play/sectionplayer/SectionPlayer.vue";
import { Media } from "@/store/media/mediaModel";
import WModal from "@/components/wrapper/w-modal.vue";
import WIcon from "@/components/wrapper/w-icon.vue";
import WButton from "@/components/wrapper/w-button.vue";
import ImageSection from "@/components/play/sectionplayer/ImageSection.vue";
import { createUnsavedEditingWeet, getMediasForWeet } from "@/utils/weetUtil";
import { Weet } from "@/store/weet/weetModel";
import { REMOVE_SECTION_EVENT } from "@/store/timeLine/timeLineAction";
import once from "lodash/once";
import { setActionEvent } from "@/utils/tracker";
import { ActionEvent } from "@/enum/TrackerEnum";
import SectionTitle from "@/components/play/sectionplayer/SectionTitle.vue";
import { getTitleTimeEventEvent } from "@/utils/timeLineUtils";
import { SourceVideoType } from "@/enum/SourceVideoEnum";
import { base64toString, getImageURLForSize, getThumbnailURL } from "@/utils/util";
import { RESET_SLIDES_2_VIDEO, SELECT_SLIDE, UPDATE_SLIDES } from "@/store/slides2Video/slides2VideoAction";
import { hasSlideWorkInProgress } from "@/utils/slides/slideUtil";
import { ScriptSlideLayout, Slide, SlideTemplate } from "@/store/slides2Video/slides2VideoModels";
import { OPEN_AVATAR_EDITING_CONTENT } from "@/store/AIAvatar/AIAVatarAction";
import { Avatar, AvatarBackground, AvatarVoiceAvatar } from "@/store/AIAvatar/aiavatarGenerationModel";
import {
  RECORD_MAIN_VIDEO_TYPE,
  RECORD_STICKER_FULLSCREEN,
  RECORD_STICKER_VIDEO_TYPE,
  SELECT_SEGMENT
} from "@/store/recordingState/recordStateAction";
import { closeLoading, confirmationDanger, showLoading } from "@/utils/dialog";

@Component({
  components: {SectionTitle, ImageSection, WButton, WIcon, WModal, SectionPlayer, WToolTip},
})
export default class TimeLineElementBig extends Vue {

  @Prop()
  private segment!: Segment;

  @Prop({default: 0})
  private index!: number;

  @Prop({default: true})
  private deleteAvailable!: boolean;

  @Prop({default:""})
  private buttonLabel!:string

  @Prop({default:null})
  private tooltipLabel!:string|null

  @Prop({default:false})
  private editingAvailable!:boolean

  private mustShow: boolean = false;
  private showPreview: boolean = false;


  get canEdit(){
    return this.editingAvailable && (this.isAvatarSlide || this.isAvatarOnly || this.isVoiceSlide || this.isSlideHTML) && this.transcriptForSection && this.isAvatarEnabled
  }

  get isSelected(){
    return store.getters.getSelectedSegment===this.segment
  }

  private mounted() {
    this.mustShow = true;
  }

  // horizontal shift of the section player, centered on the section but kept on screen
  private playerOffset: number = -234;

  get playerOffsetStyle(): string {
    return 'margin-left:' + this.playerOffset + 'px';
  }

  private keepPlayerOnScreen() {
    const PLAYER_WIDTH = 468;
    const MARGIN = 16;
    const trigger = this.$el.getBoundingClientRect();
    const centered = trigger.left + (trigger.width / 2) - (PLAYER_WIDTH / 2);
    const maxLeft = window.innerWidth - PLAYER_WIDTH - MARGIN;
    const left = Math.max(MARGIN, Math.min(centered, maxLeft));
    this.playerOffset = Math.round(left - trigger.left);
  }

  private activeChange(active: boolean) {
    if (active) {
      this.keepPlayerOnScreen();
      this.showPreview = true;
    } else {
      // waiting animation
      setTimeout(() => {
        this.showPreview = false;
      }, 100);
    }
  }

  get getTimeLine(): [] {
    return store.getters.getTimeEvent;
  }

  private getTooltipForSection(timeEvent: TimeEvent): string {
    if(!this.tooltipLabel) {
      const sectionTitle = this.getTitleForSection(timeEvent);
      if (sectionTitle) {
        return this.$t('createWeetComponent.timeline.drag').toString() + ': ' + sectionTitle;
      } else {
        return this.$t('createWeetComponent.timeline.drag').toString();
      }
    }else{
      return this.tooltipLabel;
    }
  }

  private getTitleForSection(timeEvent: TimeEvent): string {
    const te = getTitleTimeEventEvent(this.getTimeLine, timeEvent);
    if (te) {
      return te.value;
    } else {
      return '';
    }
  }

  get getWeet(): Weet {
    return createUnsavedEditingWeet(store.getters.getEditingWeetID);
  }

  get getMediaForSegment(): Media | undefined {
    if (this.segment.start.secondMedia.mediaID) {
      return getMediasForWeet(store.getters.getMyWeet(store.getters.getEditingWeetID), this.segment.start.secondMedia.mediaID);
    } else {
      return getMediasForWeet(store.getters.getMyWeet(store.getters.getEditingWeetID), this.segment.start.mainMedia.mediaID);
    }
  }

  get getMedia(): Media[] {
    const weet = store.getters.getMyWeet(store.getters.getEditingWeetID);
    if (weet) {
      return store.getters.getMedias.concat(weet.medias);
    } else {
      return store.getters.getMedias;
    }
  }

  get getTime(): number {
    return this.segment.end.time - this.segment.start.time;
  }

  get getHumanTime(): string {
    return prettyMilliseconds(this.getTime, {secondsDecimalDigits: 0, colonNotation: true});
  }


  // Manage editing

  get mainMediaForSection():Media|undefined {
    const mainMediaID=this.segment.start.mainMedia.mediaID;
    return store.getters.getMediasForMediaId(mainMediaID);

  }
  get secondMediaForSection():Media|undefined {
    const secondMediaID=this.segment.start.secondMedia.mediaID;
    return store.getters.getMediasForMediaId(secondMediaID);
  }
  get isAvatarOnly():boolean{
    const mainMedia=this.mainMediaForSection;
    const secondMedia=this.secondMediaForSection;
    if(mainMedia){
      return mainMedia.type===SourceVideoType.AIAVATAR && !secondMedia
    }
    return false;
  }
  get isAvatarSlide():boolean{
    const mainMedia=this.mainMediaForSection;
    const secondMedia=this.secondMediaForSection;
    if(mainMedia && secondMedia){
      return mainMedia.type===SourceVideoType.VIDEO_SLIDE && secondMedia.type===SourceVideoType.AIAVATAR;
    }
    return false;
  }
  get isVoiceSlide():boolean{
    const mainMedia=this.mainMediaForSection;
    const secondMedia=this.secondMediaForSection;
    if(mainMedia && secondMedia){
      return mainMedia.type===SourceVideoType.VIDEO_SLIDE && secondMedia.type===SourceVideoType.UPLOAD;
    }
    return false;
  }

  get isSlideHTML():boolean{
    const mainMedia=this.mainMediaForSection;
    if(mainMedia){
      return mainMedia.type===SourceVideoType.SLIDE || mainMedia.type===SourceVideoType.SLIDE_HTML;
    }
    return false;
  }

  get transcriptForSection():string|undefined{
    const mainMedia=this.mainMediaForSection;
    const secondMedia=this.secondMediaForSection;
    const transcriptB64=mainMedia?.transcriptB64||secondMedia?.transcriptB64;
    if(transcriptB64){
      return base64toString(transcriptB64);
    }
    return undefined;
  }

  get isAvatarEnabled():boolean{
    if(!this.isAIAvatarAuthorized){
      return false;
    }
    if(this.timeRemaining<=0){
      return false;
    }
    return true;
  }

  get timeRemaining(){
    return store.getters.getRemainingAiCredits;
  }
  get isAIAvatarAuthorized(){
    return store.getters.isAvatarAI;
  }

  private closeDropDown() {
    // @ts-ignore
    this.$refs.dropdown.toggle();
  }
  @Emit('action')
  private actionButton(segment:Segment){
    this.closeDropDown();
    return;
  }

  private deleteASection(time: number) {
    this.$buefy.dialog.confirm({
      title: this.$t('sectionDelete.dialog.title').toString(),
      message: this.$t('sectionDelete.dialog.confirm').toString(),
      confirmText: this.$t('sectionDelete.dialog.button').toString(),
      type: 'is-danger',
      hasIcon: true,
      onConfirm: once(() => {
        // Deleting the currently-selected section must clear the selection, otherwise the edition
        // panel keeps showing "Replace section" instead of "Generate section" (stale selectedSegment).
        const wasSelected = store.getters.getSelectedSegment === this.segment;
        store.dispatch(REMOVE_SECTION_EVENT, time);
        if (wasSelected) {
          store.dispatch(SELECT_SEGMENT, null);
        }
        setActionEvent(ActionEvent.record_remove_section);

      })
    });
  }


  private async editSection(withCloseDropDown:boolean=false){
    // loading this section into the editor throws away the deck being edited:
    // ask first, and before showLoading() so the dialog is not hidden behind the overlay
    if (hasSlideWorkInProgress()) {
      try {
        await confirmationDanger(
            this.$t('sectionEdit.editWithSlides.description').toString(),
            this.$t('sectionEdit.editWithSlides.title').toString(),
            this.$t('sectionEdit.editWithSlides.confirm').toString(),
            this.$t('generic.cancel').toString(),
        );
      } catch (e) {
        // the user cancelled: keep the current slides, don't load the section
        return;
      }
    }
    showLoading()
    await store.dispatch(RESET_SLIDES_2_VIDEO)
    await store.dispatch(UPDATE_SLIDES,[])
    await store.dispatch(RECORD_STICKER_VIDEO_TYPE, SourceVideoType.NONE);
    // remove sceeen if already and ask for another screen
    await store.dispatch(RECORD_MAIN_VIDEO_TYPE, SourceVideoType.NONE)
    await store.dispatch(RECORD_MAIN_VIDEO_TYPE, SourceVideoType.AIAVATAR);
    // remove fullscreen if need
    if (store.getters.isStickerFullscreen) {
      await store.dispatch(RECORD_STICKER_FULLSCREEN);
    }

    if(withCloseDropDown) {
      this.closeDropDown()
    }

    // define if the section is an avatar or a voice over
    if(this.isAvatarSlide || this.isVoiceSlide || this.isSlideHTML || this.isAvatarOnly){
      const slide = new Slide();
      if(this.mainMediaForSection?.exportStaticUrl) {
        const slideURL = getImageURLForSize(this.mainMediaForSection?.exportStaticUrl || '', 1920);


        // Download the original document to convert it in base64
        if (slideURL) {
          try {
            // Fetch the image from the URL
            const response = await fetch(slideURL);
            const blob = await response.blob();

            // Convert the blob to base64
            const reader = new FileReader();
            reader.readAsDataURL(blob);

            await new Promise((resolve) => {
              reader.onloadend = () => {
                // Get the base64 string (remove the data URL prefix)
                const base64data = reader.result as string;
                // remove mimetype
                slide.originalDocumentB64 = base64data.split("base64,")[1];
                resolve(null);
              };
            });
          } catch (error) {
            console.error('Error downloading and converting slide:', error);
          }
        }
      }

      // Download slide content
      if(this.mainMediaForSection?.slideImageUrl) {
        const slideContentImageURL = getImageURLForSize(this.mainMediaForSection?.slideImageUrl || '', 1920);
        try {
          // Fetch the image from the URL
          const response = await fetch(slideContentImageURL);
          if (!response.ok) { // image not found
            slide.slideImageB64 = "";
            console.warn('Image not found:', slideContentImageURL);
          } else {
            const blob = await response.blob();
            // Convert the blob to base64
            const reader = new FileReader();
            reader.readAsDataURL(blob);

            await new Promise((resolve) => {
              reader.onloadend = () => {
                // Get the base64 string (remove the data URL prefix)
                const base64data = reader.result as string;
                // remove mimetype
                slide.slideImageB64 = "data:image/png;base64," + base64data.split("base64,")[1];
                resolve(null);
              };
            });
          }
        } catch (error) {
          console.error('Error downloading and converting slide:', error);
        }
      }

      slide.script=this.transcriptForSection||"";
      slide.layout=(this.mainMediaForSection?.scriptSlideLayout as ScriptSlideLayout)||ScriptSlideLayout.VOICE_ONLY
      slide.slideTitle=this.mainMediaForSection?.title||"";
      slide.slideDescription=this.mainMediaForSection?.description||"";
      slide.slideTheme=this.mainMediaForSection?.theme||"";
      slide.slideBackgroundColor=this.mainMediaForSection?.slideBackgroundColor||"";
      slide.slideTemplate=this.mainMediaForSection?.slideTemplate||""
      slide.promptIllustrationGeneration=this.mainMediaForSection?.promptIllustrationGeneration||""
      if(this.mainMediaForSection?.slideTemplate===SlideTemplate.ILLUSTRATED_ONLY
        || this.mainMediaForSection?.slideTemplate===SlideTemplate.ILLUSTRATED_LEFT
        || this.mainMediaForSection?.slideTemplate===SlideTemplate.ILLUSTRATED_RIGHT
        || this.mainMediaForSection?.slideTemplate===SlideTemplate.TITLE_ILLUSTRATED ){
        slide.slideVideoUrl=this.mainMediaForSection?.cdnMediaUrl||""
      }else{
        slide.chunkVideoUrl=this.mainMediaForSection?.cdnMediaUrl||""
      }
      slide.videoTimestampStart=this.mainMediaForSection?.videoTimestampStart||-1
      slide.videoTimestampEnd=this.mainMediaForSection?.videoTimestampEnd||-1
      slide.avatar=(this.secondMediaForSection?.avatar as Avatar) || (this.mainMediaForSection?.avatar as Avatar) || null;
      slide.voice=(this.secondMediaForSection?.voice as AvatarVoiceAvatar) || (this.mainMediaForSection?.voice as AvatarVoiceAvatar) || null;
      slide.avatarBackground=(this.secondMediaForSection?.avatarBackground as AvatarBackground) || (this.mainMediaForSection?.avatarBackground as AvatarBackground) || null;
      slide.order=1;
      slide.fileID=this.mainMediaForSection?.fileID||""
      if(slide.fileID){
        slide.documentType="VIDEO";
      }

      store.dispatch(UPDATE_SLIDES,[slide])
      store.dispatch(SELECT_SLIDE,0);
      store.dispatch(OPEN_AVATAR_EDITING_CONTENT,true)
      store.dispatch(SELECT_SEGMENT,this.segment)
      closeLoading()
    }
  }
}
</script>
<style lang="scss">

// Find a better way to do that
.dropDownTimeLineBigElement {
  .dropdown-trigger {
    width: 100%;
    height: 100%;
  }

  .dropdown-menu {
    animation-duration: 0.2s;

    .dropdown-content {
      background: none;
      box-shadow: none;

      .dropdown-item {
        padding: 8px;
      }
    }

  }
}
</style>
<style scoped lang="scss">
@import "@/scss/shadows.scss";

.segmentBigItem {
  width: 100%;
  animation-duration: 0.2s;
  // padding for the hover catch
  cursor: pointer;
  animation-delay: 0.1s;
  height: 72px;
  .editSectionButton{
    position: absolute;
    top: 2px;
    right: 18px;
    cursor: pointer;
    color: white;
    font-size: 13px;
    &:hover {
      opacity: 0.8;
      z-index: 10;

    }
  }
  &.hover{
    .editSectionButton{
      z-index: 10;
    }
  }
}



.buttonDeleteSection {
  width: 18px;
  height: 18px;
  position: absolute;
  top: 2px;
  right: -2px;
  // background: red;
  .iconClose {
    position: absolute;
    top: 0px;
    left: 0px;
  }

  &:hover {
    opacity: 0.8;
  }
}

.titleSection {
  position: absolute;
  text-align: left;
  margin-left: 4px;
  width: calc(100% - 24px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: white;
  font-size: 11px;
  height: 16px;
  top: 0px;
  left: 0px;

}

.close {
  border: 2px solid white;
  border-radius: 50px;
  position: absolute;
  right: -16px;
  top: -16px;
}
.buttonAction{
  margin: auto;
  margin-top: 8px;
}

.playerSectionContainer {
  text-align: center;
  width: 468px;
  top: -8px;
  border-radius: 12px;
  background: #FFFFFF;
  padding: 8px;
  position: relative;
  @extend .shadow1;

  .sectionPlayer {
    height: 254px;
    position: relative;
  }
}

.backgroundDropDown {
  // background: red;
}

.dropDownTrigger {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 8px;

  // the thumbnail clips itself, so the edit/delete buttons can reach the edge
  ::v-deep .sectionPlayerContainer {
    border-radius: 8px;
  }

  .withBorder {
    border: solid 2px var(--primary);
    border-radius: 8px;
  }

}

.dropDownTimeLineBigElement {
  width: 100%;
  height: 100%;
}

.timeSection {
  color: white;
  bottom: 2px;
  position: absolute;
  right: 6px;
  font-size: 13px;
  font-weight: 700;
}

</style>
